import React from "react";

export type AsyncWorkflow<Action, State> = (
  dispatch: React.Dispatch<Action>,
  state: State,
) => void;

export type AsyncWorkflowDispatch<State, Action> = (
  actionOrFunction: Action | AsyncWorkflow<Action, State>,
) => void;

/**
 * Adds the ability to dispatch functions in addition to actions to enable async workflows with useReducer hook
 * @param reducer
 * @param initialState
 * @example
 * const [state, dispatch] = useModel((state, action) => action, { count: 1 })
 *  dispatch((dispatch, state) => {
 * // do side effects then call dispatch parameter
 * dispatch({ type: "done!" })
 * })
 */
const useModel = <State, Action>(
  reducer: React.Reducer<State, Action>,
  initialState: State,
): [State, AsyncWorkflowDispatch<State, Action>] => {
  const [state, _dispatch] = React.useReducer(reducer, initialState);
  const dispatch: AsyncWorkflowDispatch<State, Action> = (
    action: Action | AsyncWorkflow<Action, State>,
  ) => {
    if (typeof action === "function") {
      return (action as AsyncWorkflow<Action, State>)(_dispatch, state);
    } else return _dispatch(action);
  };

  return [state, dispatch];
};

export default useModel;
